import { api } from './client';
import type {
  CreateReturnRequestDto,
  CustomerAddressDto,
  CustomerAddressUpsertDto,
  OrderDetailDto,
  OrderListItemDto,
  OrderTrackingDto,
  PagedRequest,
  PagedResult,
  ProductListItemDto,
  ReturnRequestDto,
  WishlistToggleResultDto,
  CustomerCouponDto,
} from './types';

/* ------------------------------------------------------------------ *
 *  Musteri hesabi — 14 uc
 *  siparisler (5), takip (1), adres defteri (5), favoriler (3)
 * ------------------------------------------------------------------ */

export const accountApi = {
  /** 1. Kendi siparislerim. */
  myOrders: (query: PagedRequest = {}) =>
    api.get<PagedResult<OrderListItemDto>>('/api/account/orders', query),

  /**
   * 2. Siparis detayi. Sunucu siparisin gercekten bu kullaniciya ait
   * oldugunu dogrular; degilse 404 doner (403 degil — siparisin varligi
   * bile sizmasin diye).
   */
  myOrder: (publicId: string) => api.get<OrderDetailDto>(`/api/account/orders/${publicId}`),

  /** 3. Musterinin kendi siparisini iptal etmesi. */
  cancelMyOrder: (publicId: string, reason?: string) =>
    api.postCommand(`/api/account/orders/${publicId}/cancel`, { reason: reason ?? null }),

  /** 4. Iade taleplerim. */
  myReturns: () => api.get<ReturnRequestDto[]>('/api/account/orders/returns'),

  /** 5. Iade talebi acar. */
  createReturn: (dto: CreateReturnRequestDto) =>
    api.post<ReturnRequestDto>('/api/account/orders/returns', dto),

  /** 6. Misafir siparis takibi: siparis numarasi + e-posta ile dogrulanir. */
  trackOrder: (orderNumber: string, email: string) =>
    api.post<OrderTrackingDto>('/api/order-tracking', { orderNumber, email }),

  /** 6b. Misafir siparis takibi: publicId GUID ile dogrulanir. */
  trackOrderByPublicId: (publicId: string) =>
    api.get<OrderTrackingDto>(`/api/order-tracking/${publicId}`),

  /** 7. Adres defteri. */
  addresses: () => api.get<CustomerAddressDto[]>('/api/account/addresses'),

  /** 8. Yeni adres. */
  createAddress: (dto: CustomerAddressUpsertDto) =>
    api.post<CustomerAddressDto>('/api/account/addresses', dto),

  /** 9. Adres gunceller. */
  updateAddress: (addressId: number, dto: CustomerAddressUpsertDto) =>
    api.put<CustomerAddressDto>(`/api/account/addresses/${addressId}`, dto),

  /** 10. Adresi siler. */
  deleteAddress: (addressId: number) =>
    api.deleteCommand(`/api/account/addresses/${addressId}/delete`),

  /** 11. Varsayilan teslimat veya fatura adresi yapar. */
  setDefaultAddress: (addressId: number, shipping = true) =>
    api.postCommand(`/api/account/addresses/${addressId}/default?shipping=${shipping}`),

  /** 12. Favori urunler. */
  wishlist: () => api.get<ProductListItemDto[]>('/api/account/wishlist'),

  /** 13. Favorideki urun ID listesi. */
  wishlistIds: () => api.get<number[]>('/api/account/wishlist/ids'),

  /** 14. Favori ekle/cikar (toggle). */
  toggleWishlist: (productId: number, variantId?: number | null) =>
    api.post<WishlistToggleResultDto>('/api/account/wishlist/toggle', {
      productId,
      variantId: variantId ?? null,
    }),

  /** 15. Favori bildirim tercihlerini gunceller (stok/fiyat). */
  updateWishlistNotifications: (
    productId: number,
    notifyOnRestock?: boolean,
    notifyOnPriceDrop?: boolean,
  ) =>
    api.putCommand('/api/account/wishlist/notifications', {
      productId,
      notifyOnRestock,
      notifyOnPriceDrop,
    }),

  /** 16. Favorilere ekler. */
  addToWishlist: (productId: number, variantId?: number | null) =>
    api.postCommand('/api/account/wishlist', { productId, variantId: variantId ?? null }),

  /** 17. Favorilerden cikarir. */
  removeFromWishlist: (productId: number, variantId?: number | null) =>
    api.deleteCommand('/api/account/wishlist/remove', { productId, variantId: variantId ?? undefined }),

  /** 18. Hesabıma tanımlı kuponlar ve indirimler. */
  coupons: () => api.get<CustomerCouponDto[]>('/api/account/coupons'),
};
